import React, { forwardRef, useEffect, useRef, useState } from 'react';
import moment from 'moment';

import { CaretDown } from '~/ui/assets/icons';
import { Calendar, CalendarValue } from '~/ui/components';
import useOutsideClickEffect from '~/common/useOutsideClickEffect';

import {
  SelectButton,
  SelectContainer,
  SelectOptions,
  ActionIcons,
  CloseButton,
  ValueContainer,
  SelectLabel,
  SelectValue,
  Input,
} from './SelectDate.styled';

export type SelectDateProps = {
  placeholder?: string;
  icon?: React.ReactElement;
  onChange?: (value: CalendarValue) => void;
  selectRange?: boolean;
  value?: CalendarValue;
  asFilter?: boolean;
  typeable?: boolean;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  label?: string;
  width?: string | number;
  clearable?: boolean;
  dateFormat?: string;
};

export const SelectDate = forwardRef(
  (
    {
      placeholder,
      icon,
      onChange,
      selectRange,
      value,
      typeable = false,
      maxDate,
      minDate,
      disabled,
      dateFormat,
      label,
      width = '100%',
      clearable = true,
    }: SelectDateProps,
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [currentValue, setCurrentValue] = useState<CalendarValue>(value);
    const containerRef = useRef(ref);
    const inputRef = useRef(null);

    const focusInput = () => {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    };

    const handleToggleOpen = (e) => {
      const isCloseClick =
        e.target?.classList?.contains?.('close') ||
        e.target?.parentElement?.classList?.contains?.('close') ||
        e.target?.parentElement?.parentElement?.classList?.contains?.('close');

      setOpen(!isCloseClick);

      focusInput();
    };

    const handleClearValue = () => {
      setCurrentValue(null);
      setOpen(false);
      onChange?.(null);
    };

    const handleCalendarChange = (value) => {
      setCurrentValue(value);
      onChange?.(value);
      setOpen(false);
    };

    const handleInputChange = (e) => {
      const inputValue = e.target.value;
      const format = 'YYYY-MM-DD';
      const year = inputValue.slice(0, 4);

      if (moment(inputValue, format, true).isValid()) {
        setCurrentValue(inputValue);
        onChange?.(inputValue);
      }

      if (year.length === 4 && year > 1000) {
        setOpen(false);
      }
    };

    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        setOpen(false);
      }
    };

    const renderDate = (date) => moment(date).format(dateFormat || 'DD/MM/YYYY');

    const renderValue = () => {
      if (Array.isArray(currentValue)) {
        return `${renderDate(currentValue[0])} - ${renderDate(currentValue[1])}`;
      }

      return renderDate(currentValue);
    };

    useOutsideClickEffect(containerRef, () => {
      setOpen(false);
    });

    useEffect(() => {
      setCurrentValue(value);
    }, [value]);

    return (
      <SelectContainer ref={containerRef} disabled={disabled} width={width}>
        <SelectButton onClick={handleToggleOpen} open={open} withValue={!!currentValue} clearable={clearable}>
          <ValueContainer>
            <SelectLabel withValue={!!currentValue || (typeable && open)} hasIcon={!!icon}>
              {label || placeholder}
            </SelectLabel>
            {open && typeable && (
              <Input ref={inputRef} type="date" onChange={handleInputChange} onKeyDown={handleKeyPress} />
            )}
            {icon && icon}
            {currentValue && <SelectValue>{renderValue()}</SelectValue>}
          </ValueContainer>

          <ActionIcons>
            <CloseButton className="close" onClick={handleClearValue} />
            <CaretDown className="caret" />
          </ActionIcons>
        </SelectButton>

        {open && (
          <SelectOptions>
            <Calendar
              maxDate={maxDate}
              minDate={minDate}
              selectRange={selectRange}
              defaultValue={currentValue}
              onChange={handleCalendarChange}
            />
          </SelectOptions>
        )}
      </SelectContainer>
    );
  },
);
